import { useState, useEffect, useMemo, useRef } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Users, ArrowRight, AlertTriangle } from "lucide-react";
import { fmtTime, getOrgTimeZone, todayKey } from "@/lib/time";
import { pausedDriverIds } from "@/lib/pausedDrivers";
import { useConfirm } from "@/lib/confirm";
import { clearDriverLoad } from "@/lib/driverLoad";
import { suggestRuns, pickDriver, WINDOW_MIN, MAX_RIDE_MIN } from "@/lib/groupRuns";

const sel = "rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-600";
const onShift = (d, iso) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: getOrgTimeZone(), weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  const s = (d.shift_template || {})[String(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(p.weekday))];
  const hm = `${p.hour}:${p.minute}`;
  return !!s && s.start <= hm && hm < s.end;
};
const pref = (k, v) => { const s = localStorage.getItem(k); return s == null ? v : s === "1"; };

export default function GroupRunsDialog({ open, onOpenChange, trips, dayTrips, date, onDone }) {
  const [drivers, setDrivers] = useState(null);
  const [abaOnly, setAbaOnly] = useState(() => pref("rs_runs_aba", true));
  const [auto, setAuto] = useState(() => pref("rs_runs_auto", false));
  const [pick, setPick] = useState({});
  const [on, setOn] = useState({});
  const [busy, setBusy] = useState(false);
  const ask = useConfirm();
  const autoRan = useRef(false);

  useEffect(() => {
    if (!open) return;
    setDrivers(null); setPick({}); setOn({}); autoRan.current = false;
    Promise.all([fleetApi.get("/drivers"), fleetApi.get("/vehicles").catch(() => ({ data: [] })), pausedDriverIds()]).then(([r, v, p]) => {
      const veh = Object.fromEntries((v.data || []).filter((x) => x.current_driver_id && x.status !== "retired").map((x) => [x.current_driver_id, x]));
      setDrivers((r.data || []).filter((d) => d.status !== "retired" && !p.has(d.id)).map((d) => ({ ...d, veh: veh[d.id] })));
    }).catch(() => setDrivers([]));
  }, [open]);

  const maxSeats = useMemo(() => Math.max(1, ...(drivers || []).map((d) => d.veh?.capacity || 0)), [drivers]);
  const runs = useMemo(() => (drivers ? suggestRuns(trips || [], { abaOnly, maxSeats }) : []), [drivers, trips, abaOnly, maxSeats]);
  const auto0 = useMemo(() => {
    const busyMs = {};
    (dayTrips || []).filter((t) => t.assigned_driver_id && t.scheduled_pickup && !/complete|cancel|no.?show/i.test(t.status || ""))
      .forEach((t) => { (busyMs[t.assigned_driver_id] = busyMs[t.assigned_driver_id] || []).push(new Date(t.scheduled_pickup).getTime()); });
    const taken = {};
    return Object.fromEntries(runs.map((r) => {
      const d = pickDriver(r, (drivers || []).filter((x) => onShift(x, r.start)), busyMs, taken);
      if (d) { const s = new Date(r.start).getTime(); (taken[d.id] = taken[d.id] || []).push([s, s + (r.total + 15) * 60000]); }
      return [r.id, d?.id || ""];
    }));
  }, [runs, drivers, dayTrips]);

  useEffect(() => {
    // keep the owner's edits; only seed runs we haven't seen yet
    setPick((p) => ({ ...auto0, ...Object.fromEntries(Object.entries(p).filter(([k]) => k in auto0)) }));
    setOn((o) => ({ ...Object.fromEntries(Object.entries(auto0).map(([k, v]) => [k, !!v])), ...Object.fromEntries(Object.entries(o).filter(([k]) => k in auto0)) }));
  }, [auto0]);

  const chosen = runs.filter((r) => on[r.id] && pick[r.id]);
  const driverOf = (id) => (drivers || []).find((d) => d.id === id);

  const assign = async (list, confirmFirst = true, picks = pick) => {
    if (!list.length) return;
    const kids = list.reduce((s, r) => s + r.stops.length, 0);
    if (confirmFirst && !(await ask({ title: `Assign ${list.length} group run${list.length === 1 ? "" : "s"}?`, description: `${kids} trips go to ${list.length} driver${list.length === 1 ? "" : "s"}, in stop order.`, confirmLabel: "Assign runs" }))) return;
    setBusy(true);
    let ok = 0;
    for (const r of list) {
      const d = driverOf(picks[r.id]);
      if (!d) continue;
      try {
        for (const s of r.stops) await fleetApi.post(`/trips/${s.trip.id}/assign`, { driver_id: d.id, reason: `Group run to ${r.centerName}`, override: true });
        await api.post("/runs", { date: date || todayKey(), direction: r.direction, center: r.centerName || "", driver_id: d.id, driver_name: d.full_name || d.name, vehicle: d.veh?.fleet_number || "",
          stops: r.stops.map((s) => ({ trip_id: s.trip.id, eta: new Date(new Date(r.start).getTime() + s.offset * 60000).toISOString() })) }).catch(() => {});
        ok += 1;
      } catch (e) { toast.error(fleetError(e, `Couldn't assign the run to ${r.centerName}.`)); }
    }
    clearDriverLoad();
    setBusy(false);
    if (ok) { toast.success(`${ok} group run${ok === 1 ? "" : "s"} assigned.`); onDone?.(); onOpenChange(false); }
  };

  useEffect(() => {
    if (open && auto && !autoRan.current && !busy && drivers && runs.length && Object.keys(auto0).length) {
      const ready = runs.filter((r) => auto0[r.id]);
      if (ready.length) { autoRan.current = true; assign(ready, false, auto0); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto0]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-[#0f172a] text-white sm:max-w-3xl" data-testid="group-runs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-blue-400" /> Group runs</DialogTitle>
          <DialogDescription className="text-slate-400">Riders going to (or leaving) the same place within {WINDOW_MIN} min share one van. Nobody rides longer than {MAX_RIDE_MIN} min.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-4 rounded-lg border border-slate-800 bg-slate-900/60 p-3 text-sm">
          <label className="flex items-center gap-2"><Switch checked={abaOnly} className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-700" onCheckedChange={(v) => { setAbaOnly(v); localStorage.setItem("rs_runs_aba", v ? "1" : "0"); }} data-testid="runs-aba-only" /> ABA trips only</label>
          <label className="flex items-center gap-2"><Switch checked={auto} className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-700" onCheckedChange={(v) => { setAuto(v); localStorage.setItem("rs_runs_auto", v ? "1" : "0"); }} data-testid="runs-auto-assign" /> Auto-assign runs when I open this</label>
        </div>
        {!drivers ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : !runs.length ? <p className="rounded-md border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300" data-testid="runs-empty">No trips here can share a van{abaOnly ? " — try turning off “ABA trips only”" : ""}.</p>
          : <ul className="space-y-3" data-testid="runs-list">{runs.map((r) => (
            <RunCard key={r.id} r={r} drivers={(drivers || []).filter((d) => d.veh && (d.veh.capacity || 0) >= r.seats && (!r.wheelchair || d.veh.wheelchair_accessible))}
              pick={pick[r.id] || ""} on={!!on[r.id]} onPick={(v) => setPick({ ...pick, [r.id]: v })} onToggle={() => setOn({ ...on, [r.id]: !on[r.id] })} />
          ))}</ul>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="runs-cancel">Cancel</Button>
          <Button onClick={() => assign(chosen)} disabled={busy || !chosen.length} className="gap-2 bg-emerald-600 text-white hover:bg-emerald-500" data-testid="runs-assign">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Assign {chosen.length} run{chosen.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunCard({ r, drivers, pick, on, onPick, onToggle }) {
  const start = new Date(r.start).getTime();
  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900/60 p-3" data-testid={`run-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input type="checkbox" checked={on && !!pick} disabled={!pick} onChange={onToggle} className="h-4 w-4 accent-blue-600" data-testid={`run-check-${r.id}`} />
        <span className="text-sm font-semibold">{r.direction === "to" ? "To" : "From"} {r.centerName}</span>
        <span className="font-mono-data text-xs text-slate-400">{fmtTime(r.start)} · {r.stops.length} riders · {r.seats} seats{r.wheelchair ? " · ♿" : ""} · ~{r.total} min</span>
        <select value={pick} onChange={(e) => onPick(e.target.value)} className={`${sel} ml-auto`} data-testid={`run-driver-${r.id}`}>
          <option value="">Pick a driver…</option>
          {drivers.map((d) => <option key={d.id} value={d.id}>{d.full_name || d.name} · {d.veh.fleet_number} ({d.veh.capacity} seats)</option>)}
        </select>
      </div>
      {!pick && <p className="mt-1 flex items-center gap-1 text-xs text-amber-300" data-testid={`run-no-driver-${r.id}`}><AlertTriangle className="h-3 w-3" /> No free driver on shift with a big enough van — pick one by hand.</p>}
      <ol className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-slate-300">
        {r.stops.map((s, i) => (
          <li key={s.trip.id} className="flex items-center gap-1" data-testid={`run-stop-${r.id}-${i}`}>
            <span className="rounded bg-slate-800 px-1.5 py-0.5">{i + 1}. {s.trip.rider_name}{s.trip.escort ? " +escort" : ""} · {fmtTime(new Date(start + s.offset * 60000).toISOString())} · {s.ride} min ride</span>
            {i < r.stops.length - 1 && <ArrowRight className="h-3 w-3 text-slate-600" />}
          </li>
        ))}
      </ol>
    </li>
  );
}
